import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { LOGO_URL, NAV_TREE, CONTACT } from "../../data";
import { SocialIcons } from "./SocialIcons";

const DesktopItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isActive = item.children
    ? item.children.some((c) => c.to === location.pathname)
    : location.pathname === item.to;

  if (!item.children) {
    return (
      <NavLink
        to={item.to}
        end={item.to === "/"}
        data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
        className={({ isActive: a }) =>
          `font-body text-sm tracking-wide whitespace-nowrap transition-colors duration-300 ${
            a ? "text-[#d4af37]" : "text-white/65 hover:text-[#d4af37]"
          }`
        }
      >
        {item.label}
      </NavLink>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        data-testid={`nav-dropdown-${item.label.toLowerCase()}`}
        className={`flex items-center gap-1 font-body text-sm tracking-wide whitespace-nowrap transition-colors duration-300 ${
          isActive ? "text-[#d4af37]" : "text-white/65 hover:text-[#d4af37]"
        }`}
      >
        {item.label}
        <ChevronDown size={14} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.22 }}
            className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-56"
          >
            <div className="rounded-2xl border border-[#bf953f]/25 bg-black/90 backdrop-blur-xl p-2 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
              {item.children.map((c) => (
                <NavLink
                  key={c.to}
                  to={c.to}
                  data-testid={`nav-sublink-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive: a }) =>
                    `block rounded-xl px-4 py-2.5 font-body text-sm tracking-wide transition-colors duration-200 ${
                      a ? "text-[#d4af37] bg-[#bf953f]/10" : "text-white/70 hover:text-[#d4af37] hover:bg-white/5"
                    }`
                  }
                >
                  {c.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isAtt = pathname.startsWith("/att") || pathname.startsWith("/admin");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-[#bf953f]/20 py-3"
          : "bg-transparent border-b border-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 sm:gap-4 shrink-0">
          <Link to="/" data-testid="nav-logo" className="flex items-center group">
            <img src={LOGO_URL} alt="Heritage Family Protection Agency" className="h-11 w-auto object-contain glow-gold" />
          </Link>
        </div>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_TREE.map((item) => (
            <DesktopItem key={item.label} item={item} />
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4 shrink-0">
          <a
            href={CONTACT.phoneHref}
            data-testid="nav-call-btn"
            className="btn-sweep inline-flex items-center rounded-full border border-[#bf953f]/50 px-5 py-2.5 font-body text-sm tracking-wide text-[#fcf6ba] hover:border-[#d4af37] transition-colors duration-300"
          >
            {CONTACT.phone}
          </a>
          <SocialIcons testPrefix="nav-social" />
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          className="lg:hidden text-[#d4af37] p-2"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-t border-[#bf953f]/15"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_TREE.map((item) =>
                item.children ? (
                  <div key={item.label}>
                    <p className="font-body text-[11px] tracking-[0.3em] uppercase text-[#d4af37]/70 mb-2">{item.label}</p>
                    <div className="flex flex-col gap-3 pl-3 border-l border-[#bf953f]/20">
                      {item.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          onClick={() => setOpen(false)}
                          data-testid={`nav-mobile-link-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className="font-serif-display text-xl text-white/80 hover:text-[#d4af37] transition-colors"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="font-serif-display text-2xl text-white/80 hover:text-[#d4af37] transition-colors"
                  >
                    {item.label}
                  </Link>
                )
              )}
              <a href={CONTACT.phoneHref} className="font-body text-sm tracking-widest text-[#fcf6ba] pt-2">
                {CONTACT.phone}
              </a>
              <SocialIcons testPrefix="nav-mobile-social" />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
